import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { ScrollText } from 'lucide-react';
import { TERMS_V1, TERMS_VERSION } from './referralTermsText';

/**
 * WO-71 — "Refer & Earn — how to share your code, and tax on your commissions".
 * BILAL's text of 20 October 2026, word for word (part of the Referral Programme
 * Terms; sections 1 to 4 are binding). Do NOT reword it here: any change comes from
 * BILAL. The binding text is English; Arabic screens show it in English with a note.
 */

const LICENCES = [
  ['Saudi Arabia', 'Yes. The “Mawthooq” licence is required to advertise on social media to audiences in Saudi Arabia for any reward (money, gifts or services). The individual licence is available to Saudi and other GCC nationals. Exempt: promoting your own product or service on your own account', 'General Authority for Media Regulation (GAMR)'],
  ['United Arab Emirates', 'Yes, since 1 February 2026. The “Advertiser Permit” is required for paid and unpaid promotion on social media, including referral and affiliate links. Free for UAE citizens and residents for the first three years. Exempt: promoting your own business', 'UAE Media Council'],
  ['Kuwait', 'Yes, from about April 2027, when the new Media Regulation Law (Decree-Law No. 102 of 2026) comes into force. It requires a licence to promote, with or without payment, content aimed at the public in Kuwait. Personal accounts used for personal purposes are not covered', 'Ministry of Information'],
  ['Oman', 'Yes. Influencers who carry out paid marketing and promotion on social media must hold a licence (Ministerial Decision 619/2022), and businesses that use them must check it. Merchants promoting their own products are exempt', 'Ministry of Commerce, Industry and Investment Promotion'],
  ['Qatar', 'A registration regime for “personal advertisers” has been reported since 2023, and a broader licensing law for content creators has been proposed. Treat paid public promotion as requiring a licence', 'Ministry of Culture'],
  ['Bahrain', 'No specific licence as of October 2026; proposals are under discussion. General advertising and consumer-protection rules still apply', '—'],
];

const H = ({ children }) => <h3 className="font-semibold text-base mt-5 mb-2">{children}</h3>;
const UL = ({ items }) => (
  <ul className="list-disc ps-5 space-y-1.5">{items.map((t, i) => <li key={i}>{t}</li>)}</ul>
);

export function ReferralRulesContent() {
  return (
    <div className="text-sm leading-relaxed space-y-3" dir="ltr" data-testid="referral-rules-content">
      <p className="text-muted-foreground">Mid2Bid · operated by PASTORIA PROJECT, S.L. (Spain) · Last updated: 20 October 2026</p>
      <p>
        This page is part of the Mid2Bid Referral Programme Terms. The rules in sections 1 to 4 are binding.
        The country information in section 2.3 is general information to help you, not legal or tax advice; laws
        in the Gulf are changing quickly, and you remain responsible for complying with the law where you live.
        If anything on this page conflicts with the Referral Programme Terms, the Terms prevail.
      </p>

      <H>The short version</H>
      <UL items={[
        'Share your code privately, person to person — by direct message, e-mail, messaging app or in conversation. Do not put it in public posts, and do not publish posts that invite people to ask you for it.',
        'You may talk about Mid2Bid publicly and share Mid2Bid’s posts, as long as the post contains no code, no discount and no “ask me for my code”.',
        'Most referrers receive their full commission. Tell us your country of tax residence, and tell us if it changes.',
      ]} />

      <H>1. Why these rules exist</H>
      <p>
        Most Gulf countries now require a licence or permit from anyone who promotes another business’s products
        on social media, and several of them apply that rule whether or not the person is paid. A public post that
        contains your referral code, or invites people to ask for it, is that kind of promotion: you earn a
        commission from the sales it produces.
      </p>
      <p>
        A private recommendation, made to a person you address individually, is not public advertising. That is
        why, in the Refer &amp; Earn programme, public offers are made only by Mid2Bid itself, on its own pages, and
        codes travel only privately. This lets you take part without needing any licence.
      </p>

      <H>2. Sharing your code</H>
      <p className="font-medium">2.1 What you can do</p>
      <UL items={[
        'Send your code or link to people you know, one by one — by LinkedIn message, e-mail, WhatsApp or similar, or tell them in person.',
        'Answer someone who asks you privately about Mid2Bid, and give them your code.',
        'Post publicly about Mid2Bid, reshare Mid2Bid’s posts and give your honest opinion, without a code, link with your code, discount or invitation to ask you for one.',
      ]} />
      <p className="font-medium pt-2">2.2 What you must not do</p>
      <UL items={[
        'Put your code or referral link in any public post, comment, article, profile, headline, story, video or group (including LinkedIn groups).',
        'Publish a post that invites people to ask you for your code or a discount, for example “comment below and I’ll send you my code”.',
        'Send the same message to large numbers of people, or contact people who have asked you not to.',
        'Describe yourself publicly as Mid2Bid staff, or use “Mid2Bid Ambassador” as a job title or profile headline.',
        'Make the claims listed in clause 9.2 of the Referral Programme Terms (prices, “the only platform”, figures, comparisons with competitors, promised results).',
      ]} />
      <p>
        This rule applies wherever you live, not only in the Gulf. A LinkedIn post reaches your whole network, and
        some Gulf rules apply to promotion aimed at people in that country even when the person posting lives
        elsewhere.
      </p>
      <p className="font-medium pt-2">2.3 Licence rules in the Gulf (for information, as of 20 October 2026)</p>
      <div className="space-y-2">
        {LICENCES.map(([country, rule, authority]) => (
          <div key={country} className="rounded-md border p-3">
            <p className="font-medium">{country}</p>
            <p className="text-muted-foreground">Is a licence or permit required to promote another business on social media? {rule}</p>
            <p className="text-xs text-muted-foreground mt-1">Authority: {authority}</p>
          </div>
        ))}
      </div>
      <p>
        Private, one-to-one recommendations are not covered by any of these regimes, as far as we are aware. If you
        want to promote Mid2Bid publicly with your code in any of these countries, you would need the local licence
        first — and under our Terms you may not do so in any case.
      </p>

      <H>3. Telling people you earn a commission</H>
      <p>
        When someone pays using your code, Mid2Bid tells them at checkout that the owner of the code receives a
        commission. If someone you contact asks, tell them too.
      </p>
      <p>
        If you refer the organisation you work for, and you take part in or influence its decision to buy, you earn
        commission only if the organisation knows you receive it. When the buyer is from your own organisation, we
        will ask them to confirm this.
      </p>

      <H>4. Tax</H>
      <p>
        Most referrers receive their full commission. In some cases — mainly for people who refer customers
        regularly — Spanish law may require Mid2Bid, as a Spanish company, to deduct tax from a payment. If that
        applies to you, we will tell you before you request a withdrawal, show you the amount, and explain how you
        may be able to avoid it (for example, with a tax-residence certificate from your country). The full rules
        are in clause 8 of the Referral Programme Terms.
      </p>
      <p>
        Please give us your country of tax residence truthfully on your first withdrawal request, and tell us within
        30 days if it changes. We rely on what you tell us: if wrong information leads Mid2Bid to pay tax it would
        not otherwise have paid, we may recover it from your later commissions. You are responsible for any tax on
        your commissions in your own country.
      </p>

      <H>Questions</H>
      <p>
        support@mid2bid.com — Mid2Bid is operated by PASTORIA PROJECT, S.L., CIF B57664179, Camino Son Rapiña 8,
        1ºA, 07013 Palma de Mallorca, Spain.
      </p>
    </div>
  );
}

// ---- Referral Programme Terms v1.0 (BILAL, 23/10/2026), word for word -------------
const isClauseHeading = (style, text) => (style === 'Heading2')
  || (/^\d+\. [A-Z]/.test(text) && text.length < 90 && !/\.$/.test(text));

/** Renders the Terms. `only` = 'ambassador' | 'first_fifty' shows just that section. */
export function ReferralTermsContent({ only }) {
  let items = TERMS_V1;
  if (only) {
    const head = only === 'ambassador' ? 'Section A' : 'Section F';
    const start = items.findIndex(([, t]) => t.startsWith(head));
    const next = items.findIndex(([, t], i) => i > start && t.startsWith('Section '));
    items = items.slice(start, next === -1 ? undefined : next);
  }
  return (
    <div className="text-sm leading-relaxed space-y-2" dir="ltr" data-testid="referral-terms-content">
      {items.map(([style, text], i) => (isClauseHeading(style, text)
        ? <p key={i} className="font-semibold pt-2">{text}</p>
        : <p key={i} className={style === 'FirstParagraph' && i === 0 ? 'text-muted-foreground' : ''}>{text}</p>))}
    </div>
  );
}

/** "Referral Programme Terms" link that opens them in a window. */
export function ReferralTermsLink({ isAr, className = '', label }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="referral-terms-link"
        className={`inline-flex items-center gap-1 underline underline-offset-2 hover:text-foreground ${className}`}>
        <ScrollText className="w-3.5 h-3.5" />
        {label || (isAr ? `شروط برنامج الإحالة (الإصدار ${TERMS_VERSION})` : `Referral Programme Terms (version ${TERMS_VERSION})`)}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto" data-testid="referral-terms-dialog">
          <DialogHeader>
            <DialogTitle>Mid2Bid Referral Programme Terms</DialogTitle>
            {isAr && <DialogDescription>النص المعتمد باللغة الإنجليزية.</DialogDescription>}
          </DialogHeader>
          <ReferralTermsContent />
          <p className="text-sm pt-2">See also: <ReferralRulesLink isAr={false} label="How to share your code, and tax on your commissions" /></p>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** A link that opens the sharing-and-tax page in a window. */
export function ReferralRulesLink({ isAr, className = '', label }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="referral-rules-link"
        className={`inline-flex items-center gap-1 underline underline-offset-2 hover:text-foreground ${className}`}>
        <ScrollText className="w-3.5 h-3.5" />
        {label || (isAr ? 'كيف تشارك رمزك، والضرائب' : 'How to share your code, and tax')}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto" data-testid="referral-rules-dialog">
          <DialogHeader>
            <DialogTitle>Refer &amp; Earn — how to share your code, and tax on your commissions</DialogTitle>
            {isAr && (
              <DialogDescription>هذه القواعد ملزمة ونصّها المعتمد باللغة الإنجليزية.</DialogDescription>
            )}
          </DialogHeader>
          <ReferralRulesContent />
          <p className="text-sm pt-2">See also: <ReferralTermsLink isAr={false} /></p>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ReferralRulesLink;
